/**
 * Phone number validation and country code detection.
 *
 * E.164 format: +[country code][subscriber number]
 * Max 15 digits total (including country code), min ~7 for shortest valid numbers.
 */

export interface CountryCodeEntry {
	code: string;
	country: string;
}

// Ordered longest-prefix-first so the first match is the most specific.
// Covers major calling codes; extend as needed.
const COUNTRY_CODES: CountryCodeEntry[] = [
	// 4-digit codes
	{ code: '1684', country: 'American Samoa' },
	{ code: '1670', country: 'Northern Mariana Islands' },
	{ code: '1671', country: 'Guam' },
	{ code: '1787', country: 'Puerto Rico' },
	{ code: '1939', country: 'Puerto Rico' },
	{ code: '1340', country: 'US Virgin Islands' },
	// 3-digit codes
	{ code: '353', country: 'Ireland' },
	{ code: '354', country: 'Iceland' },
	{ code: '356', country: 'Malta' },
	{ code: '358', country: 'Finland' },
	{ code: '359', country: 'Bulgaria' },
	{ code: '370', country: 'Lithuania' },
	{ code: '371', country: 'Latvia' },
	{ code: '372', country: 'Estonia' },
	{ code: '380', country: 'Ukraine' },
	{ code: '381', country: 'Serbia' },
	{ code: '385', country: 'Croatia' },
	{ code: '386', country: 'Slovenia' },
	{ code: '387', country: 'Bosnia and Herzegovina' },
	{ code: '389', country: 'North Macedonia' },
	{ code: '420', country: 'Czech Republic' },
	{ code: '421', country: 'Slovakia' },
	{ code: '852', country: 'Hong Kong' },
	{ code: '853', country: 'Macau' },
	{ code: '855', country: 'Cambodia' },
	{ code: '856', country: 'Laos' },
	{ code: '880', country: 'Bangladesh' },
	{ code: '886', country: 'Taiwan' },
	{ code: '960', country: 'Maldives' },
	{ code: '961', country: 'Lebanon' },
	{ code: '962', country: 'Jordan' },
	{ code: '963', country: 'Syria' },
	{ code: '964', country: 'Iraq' },
	{ code: '965', country: 'Kuwait' },
	{ code: '966', country: 'Saudi Arabia' },
	{ code: '967', country: 'Yemen' },
	{ code: '968', country: 'Oman' },
	{ code: '970', country: 'Palestine' },
	{ code: '971', country: 'UAE' },
	{ code: '972', country: 'Israel' },
	{ code: '973', country: 'Bahrain' },
	{ code: '974', country: 'Qatar' },
	{ code: '975', country: 'Bhutan' },
	{ code: '976', country: 'Mongolia' },
	{ code: '977', country: 'Nepal' },
	{ code: '992', country: 'Tajikistan' },
	{ code: '993', country: 'Turkmenistan' },
	{ code: '994', country: 'Azerbaijan' },
	{ code: '995', country: 'Georgia' },
	{ code: '996', country: 'Kyrgyzstan' },
	{ code: '998', country: 'Uzbekistan' },
	// 2-digit codes
	{ code: '20', country: 'Egypt' },
	{ code: '27', country: 'South Africa' },
	{ code: '30', country: 'Greece' },
	{ code: '31', country: 'Netherlands' },
	{ code: '32', country: 'Belgium' },
	{ code: '33', country: 'France' },
	{ code: '34', country: 'Spain' },
	{ code: '36', country: 'Hungary' },
	{ code: '39', country: 'Italy' },
	{ code: '40', country: 'Romania' },
	{ code: '41', country: 'Switzerland' },
	{ code: '43', country: 'Austria' },
	{ code: '44', country: 'United Kingdom' },
	{ code: '45', country: 'Denmark' },
	{ code: '46', country: 'Sweden' },
	{ code: '47', country: 'Norway' },
	{ code: '48', country: 'Poland' },
	{ code: '49', country: 'Germany' },
	{ code: '51', country: 'Peru' },
	{ code: '52', country: 'Mexico' },
	{ code: '53', country: 'Cuba' },
	{ code: '54', country: 'Argentina' },
	{ code: '55', country: 'Brazil' },
	{ code: '56', country: 'Chile' },
	{ code: '57', country: 'Colombia' },
	{ code: '58', country: 'Venezuela' },
	{ code: '60', country: 'Malaysia' },
	{ code: '61', country: 'Australia' },
	{ code: '62', country: 'Indonesia' },
	{ code: '63', country: 'Philippines' },
	{ code: '64', country: 'New Zealand' },
	{ code: '65', country: 'Singapore' },
	{ code: '66', country: 'Thailand' },
	{ code: '81', country: 'Japan' },
	{ code: '82', country: 'South Korea' },
	{ code: '84', country: 'Vietnam' },
	{ code: '86', country: 'China' },
	{ code: '90', country: 'Turkey' },
	{ code: '91', country: 'India' },
	{ code: '92', country: 'Pakistan' },
	{ code: '93', country: 'Afghanistan' },
	{ code: '94', country: 'Sri Lanka' },
	{ code: '95', country: 'Myanmar' },
	// 1-digit codes
	{ code: '1', country: 'US / Canada' },
	{ code: '7', country: 'Russia / Kazakhstan' },
];

/** Sorted alphabetically by country name — for dropdown rendering. */
export const COUNTRY_CODE_OPTIONS: CountryCodeEntry[] = [...COUNTRY_CODES].sort((a, b) => a.country.localeCompare(b.country));

/** Default country code for new inputs. */
export const DEFAULT_COUNTRY_CODE = '1';

/**
 * Strip all non-digit characters except a leading +.
 * Used by the backend validation in greenroom.js (shared E.164 rules).
 */
export function normalizePhoneNumber(input: string): string {
	const trimmed = input.trim();
	if (trimmed.startsWith('+')) {
		return '+' + trimmed.slice(1).replace(/\D/g, '');
	}
	return trimmed.replace(/\D/g, '');
}

/**
 * Extract only digits from a phone number string.
 */
function digitsOnly(input: string): string {
	return input.replace(/\D/g, '');
}

/**
 * Build a full E.164 number from a country code and local number.
 * Strips a leading trunk prefix "0" from the local number — many countries
 * (UK, France, Germany, Australia, etc.) use a domestic "0" prefix that must
 * be dropped when combining with the international country code.
 */
export function buildE164(countryCode: string, localNumber: string): string {
	let digits = digitsOnly(localNumber);
	if (digits.startsWith('0')) {
		digits = digits.slice(1);
	}
	return `+${countryCode}${digits}`;
}

/**
 * Parse a full phone number into { countryCode, localNumber }.
 * Matches against known country codes (longest-prefix-first).
 * Returns the default country code if no match.
 */
export function parsePhoneNumber(input: string): { countryCode: string; localNumber: string } {
	const digits = digitsOnly(input);
	for (const entry of COUNTRY_CODES) {
		if (digits.startsWith(entry.code)) {
			return { countryCode: entry.code, localNumber: digits.slice(entry.code.length) };
		}
	}
	return { countryCode: DEFAULT_COUNTRY_CODE, localNumber: digits };
}

/**
 * Detect the country from a phone number's leading digits.
 * Returns the country name or null if not recognized.
 * Retained for programmatic lookups; the UI uses the dropdown instead.
 */
export function detectCountry(input: string): string | null {
	const digits = digitsOnly(input);
	const codeDigits = input.trim().startsWith('+') ? digits : null;
	if (!codeDigits || codeDigits.length === 0) return null;

	for (const entry of COUNTRY_CODES) {
		if (codeDigits.startsWith(entry.code)) {
			return entry.country;
		}
	}
	return null;
}

export interface PhoneValidationResult {
	valid: boolean;
	error?: string;
}

/**
 * Validate a local phone number (without country code) for PSTN dialing.
 * The country code is selected separately via the dropdown.
 */
export function validateLocalNumber(localNumber: string, countryCode: string): PhoneValidationResult {
	const digits = digitsOnly(localNumber);

	if (!digits) {
		return { valid: false, error: 'Phone number is required' };
	}

	const totalDigits = countryCode.length + digits.length;

	if (totalDigits < 7) {
		return { valid: false, error: 'Too short' };
	}

	if (totalDigits > 15) {
		return { valid: false, error: 'Too long — maximum 15 digits (E.164)' };
	}

	// Only digits and formatting characters in local number
	if (/[^\d\s\-().]+/.test(localNumber.trim())) {
		return { valid: false, error: 'Contains invalid characters' };
	}

	return { valid: true };
}

/**
 * Validate a full E.164 phone number.
 * Used by the backend validation; frontend uses validateLocalNumber + dropdown instead.
 */
export function validatePhoneNumber(input: string): PhoneValidationResult {
	const trimmed = input.trim();

	if (!trimmed) {
		return { valid: false, error: 'Phone number is required' };
	}

	if (!/^\+[\d\s\-().]+$/.test(trimmed)) {
		return { valid: false, error: 'Must start with + and country code (e.g. +1)' };
	}

	const digits = digitsOnly(trimmed);

	if (digits.length < 7) {
		return { valid: false, error: 'Too short — minimum 7 digits' };
	}

	if (digits.length > 15) {
		return { valid: false, error: 'Too long — maximum 15 digits (E.164)' };
	}

	return { valid: true };
}
